import { useEffect, useRef } from "react";
import { motionAllowed } from "features/onboarding/art/kit";
import {
  restFrames,
  stepAt,
  Story,
} from "features/onboarding/visuals/live/story";
import type { SceneRuntime } from "features/onboarding/visuals/types";

const E2E_STATIC = import.meta.env.VITE_E2E_DISABLE_ANT_MOTION === "true";

/**
 * The walkthrough's one clock: a playhead that runs continuously (one
 * requestAnimationFrame loop) and wraps at the end of the loop. The step is
 * derived from the playhead and reported (`onStage`); a step chosen from
 * outside (a click on the list, "next event") moves the playhead there: to the
 * step's start when playing, so it continues from that point, or to the step's
 * final frame when held. With reduced motion nothing runs: the scene rests on
 * each step's final frame.
 *
 * `draw` paints the frame for a time; `redraw` is a counter the caller bumps
 * when the drawing itself changed (a resize). `suspended` (off screen) stops
 * the loop on its frame; it resumes from the same playhead.
 */
export function useLiveClock(
  story: Story,
  runtime: SceneRuntime,
  draw: (t: number) => void,
  redraw: number,
  suspended = false
): void {
  const time = useRef(0);
  const reported = useRef(0);
  const seek = useRef(runtime.seek ?? 0);
  const latest = useRef({ runtime, draw, story });
  latest.current = { runtime, draw, story };

  const report = (t: number) => {
    const { runtime: rt, story: s } = latest.current;
    const step = stepAt(s, t);
    if (step + 1 !== reported.current) {
      reported.current = step + 1;
      rt.onStage(step + 1);
    }
    const from = s.H[step];
    const to = s.H[step + 1] ?? s.loop;
    rt.onProgress(Math.min(1, Math.max(0, (t - from) / (to - from))));
  };

  const { playing, stage } = runtime;
  const seekId = runtime.seek ?? 0;
  useEffect(() => {
    const still = !motionAllowed() || E2E_STATIC;
    const target = Math.min(Math.max(stage, 1), story.H.length);
    const jumped = seekId !== seek.current;
    seek.current = seekId;
    if (reported.current === 0 || stage !== reported.current || jumped) {
      reported.current = target;
      time.current =
        playing && !still ? story.H[target - 1] : restFrames(story)[target - 1];
    }
    latest.current.draw(time.current);
    report(time.current);
    if (still || !playing || suspended) return;
    let frame = 0;
    let last = performance.now();
    const tick = (now: number) => {
      // A long gap (a background tab) does not skip the story ahead.
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      const { loop } = latest.current.story;
      time.current = (time.current + dt) % loop;
      latest.current.draw(time.current);
      report(time.current);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing, stage, seekId, story, redraw, suspended]);
}
